import React, { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Input, Form, message, Card, Descriptions, Typography, Table } from "antd";
import { CheckCircleOutlined, AuditOutlined } from "@ant-design/icons";
import CustomButton from "../../components/CustomButton";
import { getOrderChangeHistoryWeb, confirmOrderChangeWeb } from "@ifa/utils";
import type { OrderChangeWebVO } from "@ifa/utils";
import { SignaturePad, SignaturePadRef } from "../OrderForms/SignaturePad";

const { Title, Text } = Typography;

const CustomerConfirm: React.FC = () => {
  const [searchParams] = useSearchParams();
  const shareCode = searchParams.get("code");
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<OrderChangeWebVO | null>(null);
  const [verified, setVerified] = useState(false);
  const [idCardInput, setIdCardInput] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const signatureRef = useRef<SignaturePadRef>(null);
  const [signatureData, setSignatureData] = useState<string | null>(null);

  useEffect(() => {
    if (shareCode) {
      fetchData();
    }
  }, [shareCode]);

  const fetchData = async (idCard?: string) => {
    if (!shareCode) return;

    try {
      setLoading(true);
      const res = await getOrderChangeHistoryWeb({
        shareCode,
        idCard,
      });

      // 类型守卫：确保返回的是对象而不是 boolean
      if (typeof res === 'boolean') {
        throw new Error('接口返回格式错误');
      }

      setData(res);

      // 如果提供了 idCard（表示是验证操作）
      if (idCard) {
        if (res.success) {
          setVerified(true);
          message.success("身份驗證成功");
        } else {
          setVerified(false);
          message.error("身份驗證失敗，請檢查證件號碼後4位");
        }
      }
    } catch (error) {
      console.error(error);
      message.error("獲取數據失敗，請稍後重試");
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = () => {
    if (!idCardInput || idCardInput.length !== 4) {
      message.warning("請輸入證件號碼後4位");
      return;
    }
    fetchData(idCardInput);
  };

  const handleSignatureSave = (dataUrl: string) => {
    setSignatureData(dataUrl);
  };

  const handleClearSignature = () => {
    setSignatureData(null);
  };

  const handleSubmit = async () => {
    if (!signatureData) {
      message.warning("請先簽名");
      return;
    }

    if (!shareCode) {
      message.error("分享碼遺失，請重新載入頁面");
      return;
    }

    if (!idCardInput || idCardInput.length !== 4) {
      message.warning("請先完成身份驗證");
      return;
    }

    try {
      setSubmitting(true);
      await confirmOrderChangeWeb({
        shareCode,
        idCard: idCardInput,
        signature: signatureData,
      });
      message.success("提交成功");
      navigate("/h5/order/success");
    } catch (error) {
      console.error(error);
      message.error("提交失敗，請稍後重試");
    } finally {
      setSubmitting(false);
    }
  };

  const changeColumns = [
    {
      title: "變更項目",
      dataIndex: "fieldName",
      key: "fieldName",
    },
    {
      title: "原內容",
      dataIndex: "oldValue",
      key: "oldValue",
    },
    {
      title: "變更後內容",
      dataIndex: "newValue",
      key: "newValue",
      render: (text: string) => <Text type="danger">{text}</Text>,
    },
  ];

  if (!shareCode) {
    return <div className="p-4 text-center">參數錯誤：缺少分享碼</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-10">
      <div className="bg-white p-4 shadow-sm mb-4">
        <Title level={4} style={{ margin: 0, textAlign: "center" }}>
          訂單變更確認
        </Title>
      </div>

      <div className="px-4 max-w-lg mx-auto">
        <Card loading={loading} className="mb-4">
          <Descriptions title="基本資料" column={1} size="small">
            <Descriptions.Item label="訂單編號">{data?.orderNo || "-"}</Descriptions.Item>
            <Descriptions.Item label="投保人">{data?.policyholderChineseName || data?.policyholderEnglishName || "-"}</Descriptions.Item>
          </Descriptions>
        </Card>

        {!verified ? (
          <Card title="身份驗證" className="mb-4">
            <Form layout="vertical">
              <Form.Item label="請輸入投保人證件號碼後4位" required help="驗證身份後查看詳細變更內容">
                <div className="flex gap-2">
                  <Input size="large" placeholder="證件號碼後4位" maxLength={4} value={idCardInput} onChange={(e) => setIdCardInput(e.target.value)} />
                  <CustomButton bgColor="#5CCAC0" height="40px" onClick={handleVerify} loading={loading}>
                    驗證
                  </CustomButton>
                </div>
              </Form.Item>
            </Form>
          </Card>
        ) : (
          <>
            <Card title="變更詳情" className="mb-4">
              <Table dataSource={data?.changeDetails || []} columns={changeColumns} pagination={false} rowKey="id" size="small" scroll={{ x: true }} />
            </Card>

            <Card title="客戶簽名" className="mb-4">
              <div className="border border-gray-300 rounded mb-4 bg-white touch-none">
                <SignaturePad
                  ref={signatureRef}
                  width={window.innerWidth - 80} // Responsive width approximation
                  height={200}
                  onSave={handleSignatureSave}
                  onClear={handleClearSignature}
                  showControls={false}
                />
              </div>

              <div className="flex justify-between mb-4">
                <CustomButton bgColor="#fff" color="#666" style={{ border: "1px solid #d9d9d9" }} onClick={() => signatureRef.current?.clear()}>
                  清除
                </CustomButton>
                {/* We can hide Save button if we want to auto-save on submit, but SignaturePad needs explicit save call to extract data */}
                <CustomButton bgColor="#fff" color="#666" style={{ border: "1px solid #d9d9d9" }} onClick={() => signatureRef.current?.save()}>
                  確認簽名
                </CustomButton>
              </div>

              {signatureData && (
                <div className="text-green-600 flex items-center gap-2 mb-4">
                  <CheckCircleOutlined /> 簽名已儲存
                </div>
              )}

              <CustomButton
                bgColor="#5CCAC0"
                block={true}
                height="44px"
                onClick={handleSubmit}
                loading={submitting}
                disabled={!signatureData}
                icon={<AuditOutlined />}
              >
                確認並提交
              </CustomButton>
            </Card>
          </>
        )}
      </div>
    </div>
  );
};

export default CustomerConfirm;
